// ----------------------------------------------------------------------------
// iamotaTranslate
// ----------------------------------------------------------------------------
// Centralized, dynamic translation system with Handlebars templating and
// mutable filter points.
//
// Resolves strings from window.translations (configured by Shopify locales),
// interpolates variables using Handlebars templates, and allows extension
// modules to transform both variable context and rendered output.
//
// Use translate() when:
// - Displaying text that may have Shopify locale overrides
// - Building messages with dynamic values (quantities, prices, product names)
// - Applying conditional formatting (e.g., show "On Sale" only if sale price exists)
// - Allowing other modules to adjust copy or context before render
//
// Core capabilities:
// - Handlebars syntax: {{ variable }} / {% if var %}...{% endif %}
// - Conditional rendering: {% if variable %}Content{% endif %}
// - Arithmetic: {{ add price 10 }}, {{ subtract quantity 1 }}
// - Money formatting: {{ formatMoney price format='...' }} (uses Shopify format)
// - Filter hooks for pre-render variable mutation and post-render output
//   adjustment (see translations filter points in this module)
//
// Pattern: All dynamic strings passed to the user should use translate()
// with a translation_key for consistency and maintainability.
// Falls back to the provided message if key not found or translation is
// missing (with console warnings).
//
// Filter points (extend with iamotaEventHandler.addFilter):
// - `translate:variables`: Modify { } before Handlebars rendering
// - `translate:translating`: Modify template string before interpolation
// - `translate:translated`: Modify final rendered string for output
//
// Examples:
// - Basic: translate('items', 'quantity_label', { count: 3 })
// - Conditional: translate('{% if onSale %}Save {{ savings }}{% endif %}',
//     'product_label', { onSale: true, savings: '25%' })
// - Money: translate('Total: {{ formatMoney subtotal }}',
//     'checkout_total', { subtotal: 49.99 })
// - With context mutation: Attach listeners to 'translate:variables' to inject
//   locale-aware formatting, user preferences, or business logic
//
// Anti-patterns:
// - Using translate() multiple times in the same message instead of one
//   template with multiple variables
// - Passing translation keys as user-dynamic values instead of literals
// - Building HTML with translate() without proper context (use in templates/snippets,
//   not for constructing markup strings)
// - Ignoring filter hooks when extension modules need message customization
// ----------------------------------------------------------------------------

// Helpers
import Handlebars from 'handlebars';
import { formatMoney } from '@shopify/theme-currency';
import iamotaEventHandler from './iamota-event-handler';

// -------------------------------------
// Translate Functionality
// -------------------------------------

export default function translate(message, translation_key) {
  var variables = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  if (typeof window.translations !== 'object' || window.translations === null) {
    console.error('Translations object is not defined or is invalid.');
    return message;
  }
  var translation;

  // Check if the translation key exists and has been translated in Shopify
  var translationKeys = translation_key.split('.');
  var translationObject = translationKeys.reduce((obj, key) => obj === null || obj === void 0 ? void 0 : obj[key], window.translations);
  if (translationObject !== undefined) {
    translation = translationObject;
    if (translation && translation.startsWith('Translation missing')) {
      translation = message;
      console.warn("\"".concat(translation_key, "\" does not have a translation populated, falling back on default. Please populate a translation in the Shopify locale files or CMS."));
    }
  } else {
    translation = message;
    console.warn("\"".concat(translation_key, "\" is not localized, falling back on default. Please configure it in the Shopify locale files + translations.liquid."));
  }

  // Allow filtering
  variables = iamotaEventHandler.applyFiltersSync('translate:variables', variables, {
    message,
    translation_key,
    variables
  });
  translation = iamotaEventHandler.applyFiltersSync('translate:translating', translation, {
    message,
    translation_key,
    variables
  });

  // Compile the translation with Handlebars
  var template = Handlebars.compile(translation);

  // Render the template with provided variables
  var renderedTranslation = template(variables);

  // Allow filtering
  renderedTranslation = iamotaEventHandler.applyFiltersSync('translate:translated', renderedTranslation, {
    translation,
    message,
    translation_key,
    variables
  });
  return renderedTranslation;
}

// ----------------------------------------------------------------------------
// Custom Handlebars Helpers
// ----------------------------------------------------------------------------

Handlebars.registerHelper('formatMoney', function (price) {
  var options = arguments[arguments.length - 1];
  var format = options.hash.format || window.theme.settingCurrencyFormat || '{{amount}}';
  return formatMoney(price, format);
});
Handlebars.registerHelper('add', function (a, b) {
  return a + b;
});
Handlebars.registerHelper('subtract', function (a, b) {
  return a - b;
});
Handlebars.registerHelper('multiply', function (a, b) {
  return a * b;
});
Handlebars.registerHelper('divide', function (a, b) {
  var precision = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 2;
  return (a / b).toFixed(precision);
});
Handlebars.registerHelper('mod', function (a, b) {
  return a % b;
});
Handlebars.registerHelper('min', function (a, b) {
  return Math.min(a, b);
});
Handlebars.registerHelper('max', function (a, b) {
  return Math.max(a, b);
});
Handlebars.registerHelper('icon', function (name) {
  name = name.replace(/[^a-zA-Z0-9-_]/g, '') || 'minus'; // Sanitize the icon name
  return new Handlebars.SafeString("<svg class=\"icon icon__".concat(name, "\" aria-hidden=\"true\" focusable=\"false\"><use xlink:href=\"#icon-").concat(name, "\"></use></svg>"));
});